// Panel ANDON — interrumpe la rotación cuando algo requiere atención en piso
// (spec §3): paro abierto ≥ N minutos o línea activa por debajo del 60%.
// Espíritu jidoka: la pantalla deja de ser póster y pide reacción.

import type { PantallaDatos } from 'schemas';
import { fmt } from '../shared';

export type EventoAndon =
  | { tipo: 'paro'; paro: PantallaDatos['paros_abiertos'][number]; minutos: number }
  | { tipo: 'linea'; linea: PantallaDatos['lineas'][number] };

export function PanelAndon({ eventos }: { eventos: EventoAndon[] }) {
  const principal = eventos[0]!;
  const resto = eventos.slice(1, 4);

  return (
    <section
      className="flex h-full flex-col items-center justify-center gap-[3vh] px-[6vw] text-center"
      style={{ background: 'var(--crit-soft)' }}
    >
      <div className="flex items-center gap-3">
        <span className="relative flex h-3.5 w-3.5" aria-hidden="true">
          <span
            className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70"
            style={{ background: 'var(--crit)' }}
          />
          <span
            className="relative inline-flex h-3.5 w-3.5 rounded-full"
            style={{ background: 'var(--crit)' }}
          />
        </span>
        <span className="text-[0.85rem] tracking-[0.28em]" style={{ color: 'var(--crit)' }}>
          ATENCIÓN REQUERIDA
        </span>
      </div>

      {principal.tipo === 'paro' ? (
        <>
          <h1 className="font-serif text-[clamp(2.6rem,5vw,5rem)] leading-tight text-fg">
            Paro en {principal.paro.linea_nombre}
          </h1>
          <p
            className="font-medium leading-none"
            style={{
              color: 'var(--crit)',
              fontSize: 'clamp(4rem, 9vw, 9rem)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmt.format(principal.minutos)}
            <span className="text-[0.32em] text-fg-secondary"> min</span>
          </p>
          <p className="text-[clamp(1rem,1.5vw,1.6rem)] text-fg-secondary">
            {principal.paro.causal ?? 'Sin causal registrada todavía'}
            {principal.paro.lider_nombre ? ` · ${principal.paro.lider_nombre}` : ''}
            <span className="font-mono text-fg-tertiary"> · {principal.paro.linea_codigo}</span>
          </p>
        </>
      ) : (
        <>
          <h1 className="font-serif text-[clamp(2.6rem,5vw,5rem)] leading-tight text-fg">
            {principal.linea.nombre} por debajo del 60%
          </h1>
          <p
            className="font-medium leading-none"
            style={{
              color: 'var(--crit)',
              fontSize: 'clamp(4rem, 9vw, 9rem)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {principal.linea.cumplimiento_pct === null
              ? '—'
              : Math.round(principal.linea.cumplimiento_pct)}
            <span className="text-[0.32em] text-fg-secondary">%</span>
          </p>
          <p className="text-[clamp(1rem,1.5vw,1.6rem)] text-fg-secondary">
            {principal.linea.lider_nombre ?? 'Sin líder programado'}
            <span className="font-mono text-fg-tertiary"> · {principal.linea.codigo}</span>
          </p>
        </>
      )}

      {resto.length > 0 ? (
        <div className="flex flex-wrap items-center justify-center gap-3 pt-[1vh]">
          {resto.map((e, i) => (
            <span
              key={i}
              className="rounded-md border border-border bg-surface-elevated px-4 py-2 text-[0.9rem] text-fg-secondary"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {e.tipo === 'paro'
                ? `${e.paro.linea_nombre} · paro ${fmt.format(e.minutos)} min`
                : `${e.linea.nombre} · ${e.linea.cumplimiento_pct === null ? '—' : Math.round(e.linea.cumplimiento_pct)}%`}
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
}
